import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MousePointerClick, TrendingUp, DollarSign, Link as LinkIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function VADetails() {
  const { vaId } = useParams();
  const navigate = useNavigate();
  const [va, setVa] = useState(null);
  const [clicksData, setClicksData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState('today');
  const [syncing, setSyncing] = useState(false);
  const timeframeRef = useRef(timeframe);
  
  // Keep ref in sync with state
  useEffect(() => {
    timeframeRef.current = timeframe;
  }, [timeframe]);

  useEffect(() => {
    fetchVA();
  }, [vaId]);

  useEffect(() => {
    if (va) {
      // Initial fetch (without sync - use snapshots for past periods)
      fetchClicksData(false);
      
      // Auto-refresh every 5 minutes (only useful for "today" timeframe)
      const refreshInterval = setInterval(() => {
        if (timeframeRef.current === 'today') {
          fetchClicksDataWithRef(true);
        }
      }, 300000);
      
      return () => clearInterval(refreshInterval);
    }
  }, [va]);
  
  // Refetch when timeframe changes (no sync needed - backend handles hybrid logic)
  useEffect(() => {
    if (va) {
      fetchClicksData(false);
    }
  }, [timeframe]);

  const fetchVA = async () => {
    try {
      const response = await axios.get(`${API_URL}/vas/${vaId}`);
      setVa(response.data);
    } catch (error) {
      console.error('Error fetching VA:', error);
      toast.error('Failed to fetch VA details');
      navigate('/vas');
    } finally {
      setLoading(false);
    }
  };

  const fetchClicksData = async (withSync = false) => {
    setSyncing(true);
    try {
      // Only sync for "today" timeframe when explicitly requested
      if (withSync && timeframe === 'today') {
        await axios.get(`${API_URL}/clicks/sync`);
      }
      
      const response = await axios.get(`${API_URL}/clicks/va/${vaId}`, {
        params: { timeframe }
      });
      setClicksData(response.data);
    } catch (error) {
      console.error('Error fetching clicks data:', error);
      toast.error('Failed to fetch clicks data');
    } finally {
      setSyncing(false);
    }
  };
  
  // Version using ref for interval callback
  const fetchClicksDataWithRef = async (withSync = false) => {
    setSyncing(true);
    try {
      const currentTimeframe = timeframeRef.current;
      
      // Only sync for "today" timeframe
      if (withSync && currentTimeframe === 'today') {
        await axios.get(`${API_URL}/clicks/sync`);
      }
      
      const response = await axios.get(`${API_URL}/clicks/va/${vaId}`, {
        params: { timeframe: currentTimeframe }
      });
      setClicksData(response.data);
    } catch (error) {
      console.error('Error fetching clicks data:', error);
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  const metrics = [
    {
      title: 'Total Clicks',
      value: clicksData?.total_clicks?.toLocaleString() || 0,
      icon: MousePointerClick,
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10',
      borderColor: 'border-purple-500/20',
    },
    {
      title: 'T1 Clicks',
      value: clicksData?.t1_clicks?.toLocaleString() || 0,
      icon: TrendingUp,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
      borderColor: 'border-green-500/20',
    },
    {
      title: 'Filtered Clicks',
      value: clicksData?.filtered_clicks?.toLocaleString() || 0,
      icon: LinkIcon,
      color: 'text-red-500',
      bgColor: 'bg-red-500/10',
      borderColor: 'border-red-500/20',
    },
    {
      title: 'Earnings',
      value: `$${clicksData?.earnings?.toFixed(2) || '0.00'}`,
      subValue: clicksData?.bonus > 0 ? `incl. bonus: +$${clicksData.bonus.toFixed(2)}` : null,
      icon: DollarSign,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
      borderColor: 'border-green-500/20',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          onClick={() => navigate('/vas')}
          className="text-white hover:bg-zinc-800"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-4xl md:text-5xl font-bold font-heading text-white tracking-tight" data-testid="va-name">
              {va?.name}
            </h1>
            {syncing && (
              <span className="text-xs text-zinc-500 animate-pulse">Syncing...</span>
            )}
          </div>
          <p className="text-base text-muted-foreground mt-2">{va?.email}</p>
        </div>
        <Select value={timeframe} onValueChange={setTimeframe}>
          <SelectTrigger className="w-48 bg-zinc-900/50 border-zinc-800 text-white" data-testid="timeframe-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
            <SelectItem value="today">Today</SelectItem>
            <SelectItem value="yesterday">Yesterday</SelectItem>
            <SelectItem value="this-week">This Week</SelectItem>
            <SelectItem value="last-week">Last Week</SelectItem>
            <SelectItem value="this-month">This Month</SelectItem>
            <SelectItem value="last-month">Last Month</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metrics.map((metric, index) => (
          <div
            key={index}
            className={`metric-card bg-card p-6 rounded-md border ${metric.borderColor}`}
            data-testid={`metric-${metric.title.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">
                {metric.title}
              </span>
              <div className={`${metric.bgColor} ${metric.color} p-2 rounded-md`}>
                <metric.icon className="w-5 h-5" strokeWidth={1.5} />
              </div>
            </div>
            <div className="text-5xl md:text-6xl font-bold font-heading tracking-tighter tabular-nums text-white">
              {metric.value}
            </div>
            {metric.subValue && (
              <div className="mt-2 text-sm text-green-500 font-medium">
                {metric.subValue}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="bg-card p-6 rounded-md border border-border">
        <h2 className="text-2xl font-semibold font-heading text-white mb-4">Performance Details</h2>
        <div className="space-y-4">
          <div className="flex justify-between items-center p-4 bg-zinc-900/50 rounded-md">
            <span className="text-zinc-300">Wasted Clicks</span>
            <span className="text-white font-semibold">
              {clicksData?.total_clicks > 0
                ? ((clicksData.filtered_clicks / clicksData.total_clicks) * 100).toFixed(1)
                : 0}%
            </span>
          </div>
          <div className="flex justify-between items-center p-4 bg-zinc-900/50 rounded-md">
            <span className="text-zinc-300">T1 Clicks</span>
            <span className="text-white font-semibold">
              {clicksData?.total_clicks > 0
                ? ((clicksData.t1_clicks / clicksData.total_clicks) * 100).toFixed(1)
                : 0}%
            </span>
          </div>
          <div className="flex justify-between items-center p-4 bg-zinc-900/50 rounded-md">
            <span className="text-zinc-300">T1 Click Comparison</span>
            <div className="text-right">
              {clicksData?.last_period_t1_clicks !== null && clicksData?.last_period_t1_clicks !== undefined ? (
                <>
                  <span className="text-white font-semibold">{clicksData.last_period_t1_clicks.toLocaleString()}</span>
                  <span className="text-zinc-500 text-sm ml-1">
                    ({timeframe === 'this-week' ? 'last week' : 
                      timeframe === 'this-month' ? 'last month' : 
                      timeframe === 'this-year' ? 'last year' : 
                      timeframe === 'today' ? 'yesterday' : 'prev'})
                  </span>
                  {clicksData?.t1_clicks_change !== null && clicksData?.t1_clicks_change !== undefined && (
                    <span className={`ml-2 text-sm font-medium ${clicksData.t1_clicks_change >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {clicksData.t1_clicks_change >= 0 ? '+' : ''}{clicksData.t1_clicks_change.toFixed(1)}%
                    </span>
                  )}
                </>
              ) : (
                <span className="text-zinc-500 text-sm">N/A</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}